import React, { useState, useEffect, useRef } from "react";
import {
  LineChart, Line,
  BarChart, Bar,
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer
} from "recharts";
import html2canvas from "html2canvas";
import { Button } from "react-bootstrap";
// import { getChartInsight } from "../services/aiService";

// 🎨 Color palette
const COLORS = ["#0d6efd", "#6610f2", "#198754", "#dc3545", "#ffc107", "#20c997", "#fd7e14", "#6f42c1"];
const chartTypes = ["Bar", "Line", "Area", "Pie"];

// 🧠 Auto suggest chart based on data
const suggestChartType = (stringFields, numberFields) => {
  if (stringFields.length && numberFields.length > 1) return "Bar";
  if (numberFields.length === 1) return "Pie";
  return "Line";
};

const getAllKeys = (obj, prefix = "") => {
  let keys = [];
  for (const key in obj) {
    const value = obj[key];
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      keys = keys.concat(getAllKeys(value, fullKey));
    } else {
      keys.push(fullKey);
    }
  }
  return keys;
};

const getNestedValue = (obj, path) =>
  path.split(".").reduce((acc, part) => acc && acc[part], obj);

export default function ChartGodBootstrap({ data }) {
  const [xField, setXField] = useState("");
  const [yFields, setYFields] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [chartType, setChartType] = useState("Bar");
  const [insight, setInsight] = useState("");
  const [flattenedData, setFlattenedData] = useState([]);
  const [showAllFields, setShowAllFields] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const chartRef = useRef();

  useEffect(() => {
    if (data?.length > 0) {
      const sample = data[0];
      const keys = getAllKeys(sample);

      const stringFields = keys.filter(k => typeof getNestedValue(sample, k) === "string");
      const numberFields = keys.filter(k => typeof getNestedValue(sample, k) === "number");

      setAvailableFields(keys);
      setXField(stringFields[0] || keys[0]);
      setYFields(numberFields.slice(0, 2));
      setChartType(suggestChartType(stringFields, numberFields));

      const totals = numberFields.map(k => ({
        key: k,
        total: data.reduce((sum, d) => sum + (getNestedValue(d, k) || 0), 0),
      })).sort((a, b) => b.total - a.total);

      if (totals.length) {
        const max = totals[0];
        setInsight(`“Among all fields, **${max.key}** reigns supreme with a total of ${max.total}.”`);
      }

      const flattened = data.map(row => {
        const flat = {};
        keys.forEach(k => flat[k] = getNestedValue(row, k));
        return flat;
      });
      setFlattenedData(flattened);
    }

    
  }, [data]);

  const flattenData = (data) => {
    return data.map((item) => {
      return {
        ...item,
        ...(item?.fields || {}) // optional nested unwrapping
      };
    });
  };

//   useEffect(() => {
//     const getLLMInsight = async () => {
//       try {
//         if (!data.length || !xField || !yFields?.length) {
//           console.warn("Missing required info for AI insight.");
//           return;
//         }

//         const flattened = flattenData(data); // flatten for LLM parsing
//         const insightText = await getChartInsight(flattened, xField, yFields);

//         setInsight(insightText);
//       } catch (err) {
//         console.error("🔥 Failed to fetch AI insight:", err);
//         setInsight("Couldn't fetch AI insight. Try again.");
//       }
//     };

//     getLLMInsight();
//   }, [data, xField, yFields]);

// const fetchInsight = async () => {
//     try {
//       if (!data.length || !xField || !yFields?.length) {
//         console.warn("Missing required info for AI insight.");
//         setInsight("Please select both X and Y fields.");
//         return;
//       }
  
//       const flattened = flattenData(data); // flatten for LLM parsing
//       const insightText = await getChartInsight(flattened, xField, yFields);
  
//       setInsight(insightText);
//     } catch (err) {
//       console.error("🔥 Failed to fetch AI insight:", err);
//       setInsight("Couldn't fetch AI insight. Try again.");
//     }
//   };

  const toggleY = (field) => {
    setYFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const downloadChart = () => {
    if (chartRef.current) {
      html2canvas(chartRef.current).then(canvas => {
        const link = document.createElement("a");
        link.download = "SundayChart.png";
        link.href = canvas.toDataURL();
        link.click();
      });
    }
  };

  const renderChart = () => {
    if (!xField || yFields.length === 0)
      return <p className="fst-italic text-muted">Select X and Y fields to preview the graph.</p>;

    if (chartType === "Pie") {
      if (yFields.length !== 1)
        return <p className="text-danger">Please select exactly one Y field for Pie Chart.</p>;

      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie data={flattenedData} dataKey={yFields[0]} nameKey={xField} outerRadius={120} label>
              {flattenedData.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    const ChartComponent = chartType === "Bar" ? BarChart
                        : chartType === "Line" ? LineChart
                        : AreaChart;

    const Element = (field, i) => {
      const color = COLORS[i % COLORS.length];
      const El = chartType === "Bar" ? Bar
                : chartType === "Line" ? Line
                : Area;

      return <El key={field} type="monotone" dataKey={field} stroke={color} fill={color} />;
    };

    return (
      <ResponsiveContainer width="100%" height={400}>
        <ChartComponent data={flattenedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xField} />
          <YAxis />
          <Tooltip />
          <Legend />
          {yFields.map((f, i) => Element(f, i))}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  return (
    <div className={`container my-5 p-4 border rounded shadow ${darkMode ? "bg-dark text-white" : "bg-white"}`}>
      <div className="d-flex justify-content-between align-items-center">
      <blockquote className="blockquote mb-0">
  <p><i className="bi bi-bar-chart-line"></i> Sunday Charts</p>

</blockquote>
        <div className="form-check form-switch">
          <input className="form-check-input" type="checkbox" id="darkSwitch"
            checked={darkMode} onChange={() => setDarkMode(!darkMode)} />
          <label className="form-check-label" htmlFor="darkSwitch">Dark Mode</label>
        </div>
      </div>
      <p className="text-muted mb-4">Design dynamic charts from list.</p>

      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <label className="form-label fw-bold">X-Axis Field</label>
          <select className="form-select" value={xField} onChange={e => setXField(e.target.value)}>
            {availableFields.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <br />
          <label className="form-label fw-bold">Chart Type</label>
          <select className="form-select" value={chartType} onChange={e => setChartType(e.target.value)}>
            {chartTypes.map(type => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>

        <div className="col-md-9 mb-3">
          <label className="form-label fw-bold">Y-Axis Fields</label>
          <div className="d-flex flex-wrap gap-2">
            {(showAllFields ? availableFields : availableFields.slice(0, 20)).map(f => (
              <button key={f} type="button" onClick={() => toggleY(f)}
                className={`btn btn-sm ${yFields.includes(f) ? 'btn-primary' : 'btn-outline-secondary'}`}>
                {f}
              </button>
            ))}
            {availableFields.length > 20 && (
              <button type="button" className="btn btn-sm btn-dark" onClick={() => setShowAllFields(!showAllFields)}>
               {showAllFields ? (
  <><i className="bi bi-dash"></i> View Less</>
) : (
    <><i className="bi bi-plus-lg"></i> View More</>
)}
              </button>
            )}
          </div>
        </div>
      </div>

      {insight && <div className={`alert ${darkMode ? "alert-secondary" : "alert-info"} fw-medium`}>{insight}</div>}

      <div className="chart-area mt-4" ref={chartRef}>
        {renderChart()}
      </div>
    
      
      <div className="d-flex justify-content-center mt-3 gap-2">
      {/* <Button variant="outline-dark" onClick={fetchInsight}>
    🧠 Generate AI Insight
  </Button> */}
        <Button variant="outline-success" onClick={downloadChart}>📥 Download Chart</Button>
      </div>
    </div>
  );
}